/**
 * Continuous word ruler across the whole slide: every step's words laid out on
 * one global time axis, with faint dividers where one narration segment ends and
 * the next begins. Hovering a word pops a readable zoom bubble (fixed-positioned
 * so it isn't clipped when zoomed out). The word under the playhead is lit.
 */

import { useState } from 'react';

import type { RevealStep } from '@/lib/schema.types';
import { cn } from '@/lib/utils';

import type { StepSpan } from './playback';

export const RULER_H = 20;

interface Props {
  steps: RevealStep[];
  spans: StepSpan[];
  total: number;
  pxPerMs: number;
  /** Global playhead ms, or null when idle. */
  playheadMs: number | null;
  /** Click a word → seek the playhead to its start time (global ms). */
  onSeek: (ms: number) => void;
}

export function GlobalRuler({ steps, spans, total, pxPerMs, playheadMs, onSeek }: Props) {
  const width = Math.max(1, total * pxPerMs);
  const [hover, setHover] = useState<{ text: string; x: number; y: number } | null>(null);

  return (
    <div className="relative" style={{ height: RULER_H, width }}>
      {/* segment dividers */}
      {spans.map((s, i) =>
        i === 0 ? null : (
          <span
            key={`d${i}`}
            className="absolute top-0 h-full border-l border-border"
            style={{ left: Math.round(s.start * pxPerMs) }}
          />
        ),
      )}

      {/* words (per step, offset to global) */}
      {steps.map((step, k) =>
        (step.audio?.words ?? []).map((w, i) => {
          const gStart = spans[k].start + w.start * 1000;
          const gEnd = spans[k].start + w.end * 1000;
          const left = Math.round(gStart * pxPerMs);
          const wWidth = Math.max(2, Math.round((gEnd - gStart) * pxPerMs));
          const active = playheadMs != null && playheadMs >= gStart && playheadMs < gEnd;
          return (
            <div
              key={`${k}:${i}`}
              onMouseEnter={(e) => {
                const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                setHover({ text: w.word, x: r.left + r.width / 2, y: r.top });
              }}
              onMouseLeave={() => setHover(null)}
              onClick={() => onSeek(Math.round(gStart))}
              className={cn(
                'absolute top-0 flex h-full cursor-pointer items-center overflow-hidden whitespace-nowrap px-1 text-[9px] leading-none transition-colors hover:bg-accent/10',
                active ? 'bg-accent/15 font-semibold text-accent' : 'text-fg-muted',
              )}
              style={{ left, width: wWidth }}
            >
              {w.word}
            </div>
          );
        }),
      )}

      {hover && (
        <div
          className="pointer-events-none fixed z-[80] -translate-x-1/2 -translate-y-full rounded-md bg-fg px-2 py-1 text-[12px] font-medium text-white shadow-floating"
          style={{ left: hover.x, top: hover.y - 4 }}
        >
          {hover.text}
        </div>
      )}
    </div>
  );
}
